import type { PremiumDrawUsage } from '@lub0v/santa-utils';
import { trackUmamiEvent } from '@src/utils/umami';
import { randomize, type CardInfo } from '@lub0v/santa-utils';
import { santaClient } from '@src/state/client';

function toRandomizeCards(cards: CardInfo[]) {
  return cards.map((card) => ({
    id: `${card.id}`,
    wardId: card.wardId > 0 ? `${card.wardId}` : undefined,
    drawColors: (card.drawColors || []).map((color) =>
      color.toUpperCase() === '#FFF' || color.toUpperCase() === '#FFFFFF' ? '' : color,
    ),
    exclusions: (card.exclusions || []).map((id) => `${id}`),
  }));
}

export type DrawAssignment = { id: number; wardId: number };

export function previewBoxDraw(cards: CardInfo[], circleDraw: boolean): DrawAssignment[] {
  return randomize(toRandomizeCards(cards), circleDraw).map(({ id, wardId }) => ({
    id: Number(id),
    wardId: Number(wardId),
  }));
}

export async function confirmBoxDraw(
  assignments: DrawAssignment[] | null,
  circleDraw: boolean,
  boxKey: string,
  premiumUsage: PremiumDrawUsage[] = [],
) {
  const res = await santaClient.draw(
    assignments
      ? { key: boxKey, assignments, circleDraw, premiumUsage }
      : { key: boxKey, circleDraw, confirmPreview: true },
  );
  if (res.ok) trackUmamiEvent('box_draw_conducted');
  return res.ok ? undefined : res.error?.message || 'RANDOMIZER_FAILED';
}

/** Preserve the rest of the draw when an organizer chooses a different giftee. */
export function changePreviewGiftee(
  cards: CardInfo[],
  assignments: DrawAssignment[],
  giverId: number,
  wardId: number,
  circleDraw: boolean,
): DrawAssignment[] | null {
  const previous = assignments.find((pair) => pair.id === giverId);
  const other = assignments.find((pair) => pair.wardId === wardId);
  if (!previous || !other || giverId === wardId) return null;
  if (previous.wardId === wardId) return assignments;
  const next = assignments.map((pair) => ({ ...pair }));
  const update = (id: number, receiver: number) => {
    next.find((pair) => pair.id === id)!.wardId = receiver;
  };
  update(giverId, wardId);
  if (circleDraw) {
    const moved = assignments.find((pair) => pair.id === wardId)!;
    update(wardId, previous.wardId);
    update(other.id, moved.wardId);
  } else update(other.id, previous.wardId);
  const byId = new Map(cards.map((card) => [card.id, card]));
  const colors = (card: CardInfo) =>
    (card.drawColors || []).filter(
      (color) => color && !['#FFF', '#FFFFFF'].includes(color.toUpperCase()),
    );
  const valid = (pairs: DrawAssignment[]) => !pairs.some((pair) =>
    pair.id === pair.wardId ||
    (byId.get(pair.id)!.exclusions || []).includes(pair.wardId) ||
    colors(byId.get(pair.id)!).some((color) => colors(byId.get(pair.wardId)!).includes(color)));
  if (valid(next)) return next;
  // A direct swap may give someone themselves, especially in a three-person draw.
  // Rotate three recipients instead, retaining every unaffected assignment.
  if (!circleDraw) {
    for (const third of assignments) {
      if (third.id === giverId || third.id === other.id) continue;
      const rotated = assignments.map((pair) => ({ ...pair, wardId: pair.id === giverId ? wardId : pair.id === other.id ? third.wardId : pair.id === third.id ? previous.wardId : pair.wardId }));
      if (valid(rotated)) return rotated;
    }
  }
  return null;
}

/** Insert one unassigned participant into an existing pair without moving other pairs. */
export function insertPreviewParticipant(cards: CardInfo[], assignments: DrawAssignment[], giverId: number, wardId: number): DrawAssignment[] | null {
  const giver = assignments.find(pair => pair.id === giverId);
  const santa = assignments.find(pair => pair.wardId === wardId);
  if (!giver || giver.wardId || !santa || giverId === wardId) return null;
  const next = assignments.map(pair => ({ ...pair, wardId: pair.id === giverId ? wardId : pair.id === santa.id ? giverId : pair.wardId }));
  const people = new Map(cards.map(card => [card.id, card]));
  const colors = (card: CardInfo) => (card.drawColors || []).filter(color => color && !['#FFF', '#FFFFFF'].includes(color.toUpperCase()));
  for (const pair of next.filter(pair => pair.id === giverId || pair.id === santa.id)) {
    const from = people.get(pair.id), to = people.get(pair.wardId);
    if (!from || !to || from.id === to.id || (from.exclusions || []).includes(to.id) || colors(from).some(color => colors(to).includes(color))) return null;
  }
  return next;
}
